'use client'

// libraries
import clsx from 'clsx'

// types
declare global {
	interface Window {
		dataLayer?: Array<Record<string, any>>
	}
}

// components
import Breadcrumbs from '/react4xp/components/Breadcrumbs'
import Section from '/react4xp/components/Section'
import Select from '/react4xp/components/Form/Select'
import RoundedBox from '/react4xp/components/RoundedBox'
import Link from '/react4xp/components/Link'
import Table from '/react4xp/components/Table'
import Accordions from '/react4xp/components/Accordions'
import PdfGenerator from '/react4xp/components/PdfGenerator'

// utils
import useAccordionTableWrapper from '/react4xp/utils/useAccordionTableWrapper'
import usePrintAnnex from '/react4xp/utils/usePrintAnnex'

// css
import './index.scss'

// types
interface CoursePlanProps {
    className?: string
    breadcrumbs?: {
        label: string
        url?: string
    }[]
    dropdown?: {
        label?: string
        options?: {
            label?: string
            value?: string
            disabled?: boolean
        }[]
        selected?: string
    }
    pdf?: {
        label?: string
    }
    annex?: {
        reference?: string
        heading?: string
        sectionTitle?: string
    }
    expandAllButton?: {
        expandLabel?: string
        collapseLabel?: string
    }
    items?: Record<string, {
        title?: string
        table?: {
            list?: {
                title?: string
                content?: string
            }[]
            cta?: {
                label?: string
                url?: string
                external?: boolean
            }
            ctaText?: string
        }
        accordions?: {
            title?: string
            content?: string
        }[]
    }>
}

export default function CoursePlan({
    className,
    breadcrumbs,
    dropdown,
    pdf,
    annex,
    expandAllButton,
    items
}: CoursePlanProps) {
    const selectedYear = dropdown?.selected || null
    const selectedItem = selectedYear ? items?.[selectedYear] : null

    const handleChangeYear = (value: string) => {
        if (typeof window === 'undefined' || !window.location) {
            return
        }
        const pathName = decodeURIComponent(window.location.pathname)
        const yearRegex = /(\/\d{4}(?:-\d{4})?)(\/)?$/
        let newPath: string
        if (yearRegex.test(pathName)) {
            newPath = pathName.replace(yearRegex, `/${value}`)
        } else {
            newPath = pathName.endsWith('/') ? `${pathName}${value}` : `${pathName}/${value}`
        }
        const newUrl = newPath + window.location.search
        if (newUrl !== pathName + window.location.search) {
            window.location.assign(newUrl)
        }
    }

    useAccordionTableWrapper()
    usePrintAnnex(annex)

    return (
        <div className={clsx('page-course-plan', className)}>
            <Section
                className='page-course-plan__header'
                removeMarginTop
            >
                <div className='container'>
                    
                    <div className='row'>
                        <div className='col-lg-10 offset-lg-1 col-xl-6 offset-xl-3'>

                            {breadcrumbs && (
                                <Breadcrumbs
                                    items={breadcrumbs}
                                    className='page-course-plan__breadcrumbs'
                                />
                            )}

                            {selectedItem?.title && (
                                <h1 className='h1'>
                                    {selectedItem.title}
                                </h1>
                            )}

                            {dropdown && dropdown.options && (dropdown.options.length > 1) && (
                                <Select
                                    label={dropdown.label}
                                    options={dropdown.options}
                                    selected={selectedYear || undefined}
                                    size='small'
                                    onChange={(value) => {
                                        handleChangeYear(value)
                                    }}
                                />
                            )}

                            {selectedItem?.table?.cta && (
                                <a
                                    href={selectedItem.table.cta.url}
                                    target={selectedItem.table.cta.external ? '_blank' : '_self'}
                                    className='body page-course-plan__table__cta button-black'
                                    onClick={() => {
                                        if (window.dataLayer) {
                                            window.dataLayer.push({
                                                event: 'apply_course_plan',
                                                url: selectedItem.table?.cta?.url
                                            })
                                        }
                                    }}
                                >{selectedItem.table.cta.label}</a>

                            )}

                            {!selectedItem?.table?.cta && selectedItem?.table?.ctaText && (
                                <p
                                    className='body page-course-plan__table__cta-text'
                                    dangerouslySetInnerHTML={{ __html: selectedItem.table.ctaText }}
                                />
                            )}

                        </div>
                    </div>

                </div>
            </Section>

            {selectedItem?.table && (
                <Section className='page-course-plan__table'>
                    <div className='container'>

                        {pdf && (
                            <PdfGenerator
                                label={pdf.label}
                            />
                        )}

                        <RoundedBox bgColor='green-light'>
                            <div className='row'>
                                <div className='col-lg-10 offset-lg-1 col-xl-6 offset-xl-3'>

                                    {selectedItem.table.list && (
                                        <Table list={selectedItem.table.list} />
                                    )}

                                </div>
                            </div>
                        </RoundedBox>
                    </div>
                </Section>
            )}

            {selectedItem?.accordions && (
                <Accordions
                    color='beige'
                    accordions={selectedItem.accordions}
                    expandAllButton={{
                        expandLabel: expandAllButton?.expandLabel,
                        collapseLabel: expandAllButton?.collapseLabel
                    }}
                />
            )}

        </div>
    )
}
